import {
  Box,
  Flex,
  Form,
  Radio,
  Stack,
  Input,
  Button,
  Select,
  Heading,
  RadioGroup,
  ButtonGroup,
  CurrencyInput,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  InputGroup,
  InputRightElement,
  FormHelperText
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { getMonth, getYear, isValid } from 'date-fns'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useAppSelector } from '../../../../store/hooks'
import { rateRegex } from '../../../../constants/regexes'
import { selectHomeUuid } from '../../../../store/selectors/home'
import { cancelEdit, editBalance } from '../../../../actions/loan'
import { useTrackingContext } from '../../../../providers/tracking'
import { TrackingEventConstants } from '../../../../constants/tracking'
import { createHomeLoan, updateHomeLoan } from '../../../../actions/home'
import { LoanUpdateWarning } from '../LoanUpdateWarning/LoanUpdateWarning'
import { LOAN_STATUS, LOAN_TERM_MESSAGES, LOAN_TYPE } from '../../../../constants/loan'
import {
  HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
  HOMEOWNER_DIGEST_EDIT_LOAN_MODAL
} from '../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'

export const MSG = defineMessages({
  editHead: {
    id: 'Mortgages.edit.head',
    defaultMessage: 'Edit Your Loan'
  },
  addHead: {
    id: 'Mortgages.edit.addHead',
    defaultMessage: 'Add Your Loan'
  },
  jan: {
    id: 'Mortgages.edit.months.jan',
    defaultMessage: 'January'
  },
  feb: {
    id: 'Mortgages.edit.months.feb',
    defaultMessage: 'February'
  },
  mar: {
    id: 'Mortgages.edit.months.mar',
    defaultMessage: 'March'
  },
  apr: {
    id: 'Mortgages.edit.months.apr',
    defaultMessage: 'April'
  },
  may: {
    id: 'Mortgages.edit.months.may',
    defaultMessage: 'May'
  },
  jun: {
    id: 'Mortgages.edit.months.jun',
    defaultMessage: 'June'
  },
  jul: {
    id: 'Mortgages.edit.months.jul',
    defaultMessage: 'July'
  },
  aug: {
    id: 'Mortgages.edit.months.aug',
    defaultMessage: 'August'
  },
  sep: {
    id: 'Mortgages.edit.months.sep',
    defaultMessage: 'September'
  },
  oct: {
    id: 'Mortgages.edit.months.oct',
    defaultMessage: 'October'
  },
  nov: {
    id: 'Mortgages.edit.months.nov',
    defaultMessage: 'November'
  },
  dec: {
    id: 'Mortgages.edit.months.dec',
    defaultMessage: 'December'
  },
  'FIRST CEL': {
    id: 'Mortgages.loans.type.firstCel',
    defaultMessage: 'The main (larger)'
  },
  'SECOND CEL': {
    id: 'Mortgages.loans.type.secondCel',
    defaultMessage: 'Second loan'
  },
  HELOC: {
    id: 'Mortgages.loans.type.heloc',
    defaultMessage: 'HELOC'
  },
  amount: {
    id: 'Mortgages.edit.amount',
    defaultMessage: 'Amount'
  },
  month: {
    id: 'Mortgages.edit.month',
    defaultMessage: 'Start month'
  },
  year: {
    id: 'Mortgages.edit.year',
    defaultMessage: 'Start year'
  },
  rate: {
    id: 'Mortgages.edit.interestRate',
    defaultMessage: 'Interest rate'
  },
  type: {
    id: 'Mortgages.edit.type',
    defaultMessage: 'Loan type'
  },
  term: {
    id: 'Mortgages.edit.term',
    defaultMessage: 'Loan term'
  },
  pmi: {
    id: 'Mortgages.edit.pmi',
    defaultMessage: 'Monthly mortgage insurance (PMI)'
  },
  status: {
    id: 'Mortgages.edit.status',
    defaultMessage: 'Is this loan active, expired, or deleted?'
  },
  active: {
    id: 'Mortgages.edit.active',
    defaultMessage: 'Active'
  },
  expired: {
    id: 'Mortgages.edit.expired',
    defaultMessage: 'Expired'
  },
  deleted: {
    id: 'Mortgages.edit.deleted',
    defaultMessage: 'Deleted'
  },
  save: {
    id: 'Mortgages.edit.saveButton',
    defaultMessage: 'Save'
  },
  cancel: {
    id: 'Mortgages.edit.cancelButton',
    defaultMessage: 'Cancel'
  },
  delete: {
    id: 'Mortgages.edit.deleteButton',
    defaultMessage: 'Delete'
  },
  error: {
    id: 'Mortgages.edit.error',
    defaultMessage: 'Cannot decrease loan amount below the already edited balance.'
  },
  amountRequired: {
    id: 'Mortgages.edit.amountRequired',
    defaultMessage: 'Enter original loan amount'
  },
  required: {
    id: 'Mortgages.edit.required',
    defaultMessage: 'Required'
  },
  rateRequired: {
    id: 'Mortgages.edit.rateRequired',
    defaultMessage: 'Enter valid rate (ex: 5, 3.5, 7.125)'
  },
  warningBtn: {
    id: 'Mortgages.edit.warningBtn',
    defaultMessage: 'Update balance'
  },
  warningTxt: {
    id: 'Mortgages.edit.warningTxt',
    defaultMessage:
      'You have updated your loan balance previously and you are attempting to decrease your original loan amount below that loan balance. You will need to correct your balance before decreasing the original loan amount further.'
  },
  warningHead: {
    id: 'Mortgages.edit.warningHead',
    defaultMessage: 'Loan amount cannot be decreased'
  }
})

const ARM = [10, 7, 5, 3, 1]
const FIXED = [30, 25, 22, 20, 15, 10]
const TERMS = FIXED.map(y => ({ adjustable: false, years: y })).concat(ARM.map(y => ({ adjustable: true, years: y })))
const TYPES = [LOAN_TYPE.FIRST_CEL, LOAN_TYPE.SECOND_CEL, LOAN_TYPE.HELOC]
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map(m => MSG[m])
const YEARS = Array.from(new Array(30), (x, i) => getYear(new Date()) - i)

export const EditLoan = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const homeUuid = useSelector(selectHomeUuid)
  const { trackingClient } = useTrackingContext()
  const [hideLoanTerm, setHideLoanTerm] = useState(false)
  const saving = useAppSelector(state => state.loan.saving)
  const currentLoan = useAppSelector(state => state.loan.currentLoan)
  const [showDecreasedAmountBelowThresholdWarning, setShowDecreasedAmountBelowThresholdWarning] = useState(false)

  const handleSubmit = async values => {
    trackingClient?.clientCustomized({
      customization_type: TrackingEventConstants.CLIENT_UPDATED_LOAN
    })

    const term = JSON.parse(values.term)

    const data = {
      rate: values.rate,
      status: values.status,
      amount: values.amount,
      loanType: values.type,
      pmiMonthly: values.pmi,
      homeId: currentLoan.homeId,
      adjustable: term.adjustable,
      loanUuid: currentLoan.loanId,
      date: `${values.year}-${values.month}-01`,
      termYears: term.adjustable ? null : term.years,
      armYearsInitial: term.adjustable ? term.years : null
    }

    dispatch(!currentLoan.id ? createHomeLoan(homeUuid, data) : updateHomeLoan(data))
  }

  if (!currentLoan) return null

  const currentDate = new Date(currentLoan?.date?.split('-')?.join('/')) // This ensures we get the correct date when formatting (crazy JS date nuances): https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off)

  return (
    <>
      <Heading textAlign='left' fontWeight='semibold'>
        {!currentLoan.id ? intl.formatMessage(MSG.addHead) : intl.formatMessage(MSG.editHead)}
      </Heading>
      <Form
        onSubmit={handleSubmit}
        defaultValues={{
          amount: currentLoan.amount,
          pmi: currentLoan.pmiMonthly || 0,
          status: currentLoan.status || LOAN_STATUS.CURRENT,
          rate: `${currentLoan.rate || currentLoan.rateEstimated}` || '',
          year: getYear(isValid(currentDate) ? currentDate : new Date()),
          term: JSON.stringify(
            TERMS.find(term =>
              currentLoan.adjustable
                ? term.adjustable === true && term.years === currentLoan.armYearsInitial
                : term.adjustable === false && term.years === currentLoan.termYears
            ) || TERMS[0]
          ),
          month: MONTHS[getMonth(isValid(currentDate) ? currentDate : new Date())].defaultMessage,
          type: TYPES.find(t => t === currentLoan.loanType) || TYPES[0]
        }}
      >
        {({ formState: { errors } }) => (
          <Flex gap={4} direction='column'>
            <Flex gap={4} direction={['column', null, 'row']}>
              <FormControl isInvalid={!!errors?.amount}>
                <FormLabel size='sm'>{intl.formatMessage(MSG.amount)}</FormLabel>
                <CurrencyInput
                  size='sm'
                  name='amount'
                  tracking={{
                    guid: 'fgsd7kMNZJWZas9DTh1HnB',
                    ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
                    descriptor: MSG.amount.defaultMessage
                  }}
                  rules={{
                    validate: {
                      required: (v: string) => (v && parseInt(v) > 0) || intl.formatMessage(MSG.amountRequired),
                      greaterThanAlreadyEditedBalance: (v: string) => {
                        if (currentLoan.balance && parseInt(v) <= currentLoan.balance) {
                          setShowDecreasedAmountBelowThresholdWarning(true)
                          return intl.formatMessage(MSG.error)
                        } else {
                          if (showDecreasedAmountBelowThresholdWarning) {
                            setShowDecreasedAmountBelowThresholdWarning(false)
                          }

                          return undefined
                        }
                      }
                    }
                  }}
                />
                <FormErrorMessage>{errors?.amount?.message?.toString()}</FormErrorMessage>
              </FormControl>
              <Flex flex='1 0 50%' gap={1}>
                <FormControl isInvalid={!!errors?.month}>
                  <FormLabel size='sm'>{intl.formatMessage(MSG.month)}</FormLabel>
                  <Select
                    size='sm'
                    name='month'
                    data-qa='mortgage-month-input'
                    tracking={{
                      guid: 'v1x5PNHahBea3VWNC3J5yo',
                      ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
                      descriptor: MSG.month.defaultMessage
                    }}
                  >
                    {MONTHS.map(month => (
                      <option key={month.id} value={month.defaultMessage}>
                        {intl.formatMessage(month)}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors?.month?.message?.toString()}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors?.year}>
                  <FormLabel size='sm'>{intl.formatMessage(MSG.year)}</FormLabel>
                  <Select
                    size='sm'
                    name='year'
                    data-qa='mortgage-year-input'
                    tracking={{
                      guid: 'n9hH1D8G1EBU8oqTfsm4nJ',
                      ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
                      descriptor: MSG.year.defaultMessage
                    }}
                  >
                    {YEARS.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors?.year?.message?.toString()}</FormErrorMessage>
                </FormControl>
              </Flex>
            </Flex>
            <Flex gap={4} direction={['column', null, 'row']}>
              <FormControl isInvalid={!!errors?.rate}>
                <FormLabel size='sm'>{intl.formatMessage(MSG.rate)}</FormLabel>
                <InputGroup size='xs'>
                  <Input
                    size='sm'
                    step='.001'
                    name='rate'
                    type='number'
                    data-qa='mortgage-interest-input'
                    onKeyDown={e => (e.key === 'e' || e.key === '-' || e.key === '+') && e.preventDefault()}
                    tracking={{
                      guid: '6Vezh2HVR5jocZvG3e6JQi',
                      ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
                      descriptor: MSG.rate.defaultMessage
                    }}
                    rules={{
                      required: {
                        value: true,
                        message: intl.formatMessage(MSG.rateRequired)
                      },
                      pattern: {
                        value: rateRegex,
                        message: intl.formatMessage(MSG.rateRequired)
                      }
                    }}
                  />
                  <InputRightElement mt={1} mr={2}>
                    <Icon name='percent' />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors?.rate?.message?.toString()}</FormErrorMessage>
              </FormControl>
              <Box flex='1 0 50%'>
                <FormControl isInvalid={!!errors?.type}>
                  <FormLabel size='sm'>{intl.formatMessage(MSG.type)}</FormLabel>
                  <Select
                    size='sm'
                    name='type'
                    data-qa='mortgage-type-input'
                    onChange={e =>
                      e.target.value === LOAN_TYPE.HELOC ? setHideLoanTerm(true) : setHideLoanTerm(false)
                    }
                    tracking={{
                      guid: 's436ezxN2e3Bb3H1CvPmyB',
                      ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
                      descriptor: MSG.type.defaultMessage
                    }}
                  >
                    {TYPES.map(type => (
                      <option key={type} value={type}>
                        {intl.formatMessage(MSG[type])}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors?.type?.message?.toString()}</FormErrorMessage>
                </FormControl>
              </Box>
            </Flex>
            <Flex gap={4} direction={['column', null, 'row']}>
              {!hideLoanTerm && (
                <FormControl isInvalid={!!errors?.term}>
                  <FormLabel size='sm'>{intl.formatMessage(MSG.term)}</FormLabel>
                  <Select
                    size='sm'
                    name='term'
                    data-qa='mortgage-term-input'
                    tracking={{
                      guid: 'ovhPGc4H9jMRtzeeEaPytd',
                      ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
                      descriptor: MSG.term.defaultMessage
                    }}
                  >
                    {TERMS.map(term => (
                      <option key={JSON.stringify(term)} value={JSON.stringify(term)}>
                        {intl.formatMessage(LOAN_TERM_MESSAGES[term.adjustable ? 'arm' : 'fix'], { years: term.years })}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors?.term?.message?.toString()}</FormErrorMessage>
                </FormControl>
              )}
              <Box flex='1 0 50%'>
                <FormControl isInvalid={!!errors?.pmi}>
                  <FormLabel size='sm'>{intl.formatMessage(MSG.pmi)}</FormLabel>
                  <CurrencyInput
                    size='sm'
                    name='pmi'
                    decimalScale={2}
                    tracking={{
                      guid: 'cM5ujf1MDHc5JyFdueqY7X',
                      ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
                      descriptor: MSG.pmi.defaultMessage
                    }}
                    rules={{
                      validate: {
                        required: (v: string) => v?.length > 0 || intl.formatMessage(MSG.required)
                      }
                    }}
                  />
                  <FormErrorMessage>{errors?.pmi?.message?.toString()}</FormErrorMessage>
                </FormControl>
              </Box>
            </Flex>
            {currentLoan.id && (
              <FormControl>
                <RadioGroup
                  size='sm'
                  name='status'
                  defaultValue={currentLoan.status || LOAN_STATUS.CURRENT}
                  tracking={{
                    guid: '2nVBSa6PdS4B5YVCjeu61i',
                    ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL
                  }}
                >
                  <Stack direction='row' spacing={4}>
                    <Radio value={LOAN_STATUS.CURRENT}>{intl.formatMessage(MSG.active)}</Radio>
                    <Radio value={LOAN_STATUS.EXPIRED}>{intl.formatMessage(MSG.expired)}</Radio>
                    <Radio value={LOAN_STATUS.DELETED}>{intl.formatMessage(MSG.deleted)}</Radio>
                  </Stack>
                  <FormHelperText>{intl.formatMessage(MSG.status)}</FormHelperText>
                </RadioGroup>
              </FormControl>
            )}
            {showDecreasedAmountBelowThresholdWarning && (
              <LoanUpdateWarning
                btnTxt={MSG.warningBtn}
                bodyTxt={MSG.warningTxt}
                headerTxt={MSG.warningHead}
                onClick={() => {
                  dispatch(cancelEdit())
                  dispatch(editBalance(currentLoan))
                }}
              />
            )}
            <ButtonGroup mt={8} gap={4} w='100%'>
              <Button
                flex='1 0 auto'
                isDisabled={saving}
                onClick={() => dispatch(cancelEdit())}
                tracking={{
                  guid: '3rcmK3CQ38QjFpgJaefjFA',
                  ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
                  descriptor: MSG.cancel.defaultMessage
                }}
              >
                {intl.formatMessage(MSG.cancel)}
              </Button>
              <Button
                type='submit'
                flex='1 0 auto'
                isLoading={saving}
                isDisabled={saving}
                colorScheme='primary'
                data-qa='mortgage-submit-button'
                tracking={{
                  guid: 'pL2QPSNvreYyxiQepqaJKz',
                  ui_context: currentLoan.id ? HOMEOWNER_DIGEST_EDIT_LOAN_MODAL : HOMEOWNER_DIGEST_ADD_LOAN_MODAL,
                  descriptor: MSG.save.defaultMessage
                }}
              >
                {intl.formatMessage(MSG.save)}
              </Button>
            </ButtonGroup>
          </Flex>
        )}
      </Form>
    </>
  )
}
